import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  InputBase,
  Avatar,
  Checkbox,
  Chip,
  CircularProgress,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useTranslation } from "react-i18next";
import MenuItemCard from "./MenuItemCard";
import DuplicateReviewModal from "./DuplicateReviewModal";
import { normalizeName } from "../ocrUtils";

/**
 * ConfirmMenu - Step 4: Review and confirm OCR-parsed menu data
 *
 * @param {Object}   menuData       - Full parsed menu data from OCR API
 * @param {string}   currencySymbol - Currency symbol from business location
 * @param {Object}   planLimits     - { maxCategories, maxItems } or null (unlimited)
 * @param {string}   isSubmitting   - null | "draft" | "confirm"
 * @param {Function} onSaveDraft    - (selectionData) => void
 * @param {Function} onConfirm      - (selectionData) => void
 * @param {Function} onUpgrade      - () => void
 */
const ConfirmMenu = ({
  isCatalog = false,
  menuData,
  currencySymbol = "$",
  planLimits,
  existingCategoryCount = 0,
  existingItemCount = 0,
  existingCategories = [],
  existingItems = [],
  isSubmitting,
  onSaveDraft,
  onConfirm,
  onUpgrade,
}) => {
  const { t } = useTranslation("dashboard");
  const label = isCatalog ? t("catalogue", "catalogue") : t("menu", "menu");
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    menuData.categories[0]?.id || null
  );
  const [menuName, setMenuName] = useState(menuData.menuName);
  const [isEditingName, setIsEditingName] = useState(false);
  // Local mutable copy of categories for inline edits & deletes
  const [categories, setCategories] = useState(menuData.categories);

  // Remaining slots = planLimit - existingDBCount (how many MORE can be added)
  const remainingCatSlots = planLimits?.maxCategories != null
    ? Math.max(0, planLimits.maxCategories - existingCategoryCount)
    : null;
  const remainingItemSlots = planLimits?.maxItems != null
    ? Math.max(0, planLimits.maxItems - existingItemCount)
    : null;

  // Checkboxes — pre-check only up to remaining slots (Option B)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => {
    const cats = remainingCatSlots !== null
      ? menuData.categories.slice(0, remainingCatSlots)
      : menuData.categories;
    return new Set(cats.map((c) => c.id));
  });
  const [selectedItemIds, setSelectedItemIds] = useState(() => {
    const allowedCats = remainingCatSlots !== null
      ? menuData.categories.slice(0, remainingCatSlots)
      : menuData.categories;

    let itemCounter = 0;
    return new Map(
      menuData.categories.map((cat) => {
        const isCatAllowed = allowedCats.some((c) => c.id === cat.id);
        if (!isCatAllowed) return [cat.id, new Set()];

        const allowedItems = remainingItemSlots !== null
          ? cat.items.filter(() => {
              if (itemCounter < remainingItemSlots) { itemCounter++; return true; }
              return false;
            })
          : cat.items;
        return [cat.id, new Set(allowedItems.map((i) => i.id))];
      })
    );
  });

  // ── Derived counts ───────────────────────────────────────────────────────
  const totalCategories = categories.length;
  const selectedCatCount = selectedCategoryIds.size;
  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const selectedItemCount = [...selectedCategoryIds].reduce(
    (sum, catId) => sum + (selectedItemIds.get(catId)?.size || 0),
    0
  );
  const maxCats = planLimits?.maxCategories ?? null;
  // Over limit if existing + selected exceeds plan max
  const isOverCatLimit = maxCats !== null && (existingCategoryCount + selectedCatCount) > maxCats;

  // Get items for selected category
  const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
  const items = selectedCategory?.items || [];

  // ── Checkbox handlers ────────────────────────────────────────────────────
  const handleToggleCategory = (e, catId) => {
    e.stopPropagation();
    const isCurrentlyChecked = selectedCategoryIds.has(catId);

    // If checking and remaining slots are exhausted → show upgrade dialog
    if (!isCurrentlyChecked && remainingCatSlots !== null && selectedCatCount >= remainingCatSlots) {
      onUpgrade?.(existingCategoryCount + selectedCatCount, "categories");
      return;
    }

    // Toggle category checked state
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyChecked) next.delete(catId);
      else next.add(catId);
      return next;
    });
    // Uncheck all items in category when unchecking, re-check all when checking back
    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      const cat = categories.find((c) => c.id === catId);
      if (isCurrentlyChecked) {
        // Uncheck all items
        next.set(catId, new Set());
      } else {
        // Re-check all items
        next.set(catId, new Set(cat?.items.map((i) => i.id) || []));
      }
      return next;
    });
  };

  const handleToggleItem = (itemId) => {
    const isCurrentlyChecked =
      selectedItemIds.get(selectedCategoryId)?.has(itemId) ?? false;

    // If checking (not unchecking), verify item plan limit against remaining slots
    if (!isCurrentlyChecked && remainingItemSlots !== null && selectedItemCount >= remainingItemSlots) {
      onUpgrade?.(existingItemCount + selectedItemCount, "items");
      return;
    }

    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      const catSet = new Set(next.get(selectedCategoryId) || []);
      if (catSet.has(itemId)) catSet.delete(itemId);
      else catSet.add(itemId);
      next.set(selectedCategoryId, catSet);

      // Auto-check category if at least one item is checked; uncheck if none
      const cat = categories.find((c) => c.id === selectedCategoryId);
      const allItemIds = cat?.items.map((i) => i.id) || [];
      const anyChecked = allItemIds.some((id) => catSet.has(id));
      setSelectedCategoryIds((prevCats) => {
        const nextCats = new Set(prevCats);
        if (anyChecked) nextCats.add(selectedCategoryId);
        else nextCats.delete(selectedCategoryId);
        return nextCats;
      });

      return next;
    });
  };

  // ── Duplicate detection state ────────────────────────────────────────────
  // Tracks which normalized names have been resolved so they are skipped on re-analysis
  const [resolvedDuplicateCategoryNames, setResolvedDuplicateCategoryNames] = useState(new Set());
  const [resolvedDuplicateItemNames, setResolvedDuplicateItemNames] = useState(new Set());
  // Controls visibility of the review overlay
  const [showDuplicateReview, setShowDuplicateReview] = useState(false);

  // ── Duplicate analysis (recomputed when categories or resolved sets change) ─
  const { duplicateItemGroups, duplicateCategoryGroups, duplicateCategoryKeys, duplicateItemKeys } =
    useMemo(() => {
      if (!existingCategories.length && !existingItems.length) {
        return {
          duplicateItemGroups: [],
          duplicateCategoryGroups: [],
          duplicateCategoryKeys: new Set(),
          duplicateItemKeys: new Set(),
        };
      }

      const existingCatMap = new Map(
        existingCategories.map((c) => [normalizeName(c.name), c])
      );
      const existingItemMap = new Map(
        existingItems.map((i) => [normalizeName(i.name), i])
      );

      const catGroups = [];
      const itemGroups = [];
      const catKeys = new Set();
      const itemKeys = new Set();
      const seenItemKeys = new Set(); // prevent duplicate entries for same item name

      for (const ocrCat of categories) {
        const catKey = normalizeName(ocrCat.name);
        if (!resolvedDuplicateCategoryNames.has(catKey) && existingCatMap.has(catKey)) {
          catGroups.push({
            normalizedName: catKey,
            ocrCategory: ocrCat,
            existingCategory: existingCatMap.get(catKey),
          });
          catKeys.add(catKey);
        }

        for (const ocrItem of ocrCat.items) {
          const itemKey = normalizeName(ocrItem.name);
          if (
            !resolvedDuplicateItemNames.has(itemKey) &&
            !seenItemKeys.has(itemKey) &&
            existingItemMap.has(itemKey)
          ) {
            itemGroups.push({
              normalizedName: itemKey,
              ocrItem,
              existingItem: existingItemMap.get(itemKey),
              ocrCategoryId: ocrCat.id,
            });
            itemKeys.add(itemKey);
            seenItemKeys.add(itemKey);
          }
        }
      }

      return {
        duplicateItemGroups: itemGroups,
        duplicateCategoryGroups: catGroups,
        duplicateCategoryKeys: catKeys,
        duplicateItemKeys: itemKeys,
      };
    }, [categories, existingCategories, existingItems, resolvedDuplicateCategoryNames, resolvedDuplicateItemNames]);

  const totalDuplicateCount = duplicateCategoryGroups.length + duplicateItemGroups.length;

  // Auto-close review overlay when all duplicates are resolved
  React.useEffect(() => {
    if (showDuplicateReview && totalDuplicateCount === 0) {
      setShowDuplicateReview(false);
    }
  }, [showDuplicateReview, totalDuplicateCount]);

  // ── Duplicate merge handlers ─────────────────────────────────────────────
  const handleMergeItem = (group, keepOcr) => {
    if (!keepOcr) {
      // Remove the OCR item from its category so it is not imported
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === group.ocrCategoryId
            ? { ...cat, items: cat.items.filter((i) => normalizeName(i.name) !== group.normalizedName) }
            : cat
        )
      );
      // Remove the deleted item from selectedItemIds so it no longer inflates the count
      setSelectedItemIds((prev) => {
        const next = new Map(prev);
        const catSet = new Set(next.get(group.ocrCategoryId) || []);
        catSet.delete(group.ocrItem.id);
        next.set(group.ocrCategoryId, catSet);
        return next;
      });
    }
    setResolvedDuplicateItemNames((prev) => new Set([...prev, group.normalizedName]));
  };

  const handleKeepBothItems = (group) => {
    // Keep both versions — just mark as resolved so it no longer appears
    setResolvedDuplicateItemNames((prev) => new Set([...prev, group.normalizedName]));
  };

  const handleMergeCategory = (group, keepOcr) => {
    if (!keepOcr) {
      // Remove the OCR category entirely so it is not imported
      setCategories((prev) =>
        prev.filter((cat) => normalizeName(cat.name) !== group.normalizedName)
      );
      // Remove the category and all its items from selection so counts stay accurate
      const catId = group.ocrCategory.id;
      setSelectedCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(catId);
        return next;
      });
      setSelectedItemIds((prev) => {
        const next = new Map(prev);
        next.delete(catId);
        return next;
      });
    }
    setResolvedDuplicateCategoryNames((prev) => new Set([...prev, group.normalizedName]));
  };

  const handleKeepBothCategories = (group) => {
    // Keep both versions — just mark as resolved
    setResolvedDuplicateCategoryNames((prev) => new Set([...prev, group.normalizedName]));
  };

  const handleUpdateOcrItem = (group, updatedFields) => {
    // Update the OCR item fields in-place (name/description/price edits from duplicate review)
    handleItemUpdate(group.ocrItem.id, updatedFields);
  };

  // Helper: does this category tab have any unresolved duplicate?
  const catHasDuplicate = (cat) =>
    duplicateCategoryKeys.has(normalizeName(cat.name)) ||
    cat.items.some((i) => duplicateItemKeys.has(normalizeName(i.name)));

  // ── Auto-sync selections when categories change (e.g. after duplicate merge) ─
  // 1. Removes ghost IDs (items/categories no longer in OCR data)
  // 2. Fills freed slots with newly available unchecked items/categories
  useEffect(() => {
    const validCatIds = new Set(categories.map((c) => c.id));
    const itemIdsByCat = new Map(
      categories.map((c) => [c.id, new Set(c.items.map((i) => i.id))])
    );

    setSelectedItemIds((prev) => {
      let changed = false;
      let count = 0;
      const cleaned = new Map();

      // Remove ghost entries and count currently selected valid items
      for (const [catId, itemSet] of prev) {
        if (!validCatIds.has(catId)) { changed = true; continue; }
        const validIds = itemIdsByCat.get(catId);
        const validSet = new Set([...itemSet].filter((id) => validIds.has(id)));
        if (validSet.size !== itemSet.size) changed = true;
        cleaned.set(catId, validSet);
        count += validSet.size;
      }

      // Fill freed slots with unchecked items
      if (remainingItemSlots !== null && count < remainingItemSlots) {
        for (const cat of categories) {
          if (count >= remainingItemSlots) break;
          const catSet = new Set(cleaned.get(cat.id) || []);
          for (const item of cat.items) {
            if (count >= remainingItemSlots) break;
            if (!catSet.has(item.id)) {
              catSet.add(item.id);
              count++;
              changed = true;
            }
          }
          cleaned.set(cat.id, catSet);
        }
      }

      return changed ? cleaned : prev;
    });

    setSelectedCategoryIds((prev) => {
      let changed = false;
      const cleaned = new Set(
        [...prev].filter((id) => {
          if (!validCatIds.has(id)) { changed = true; return false; }
          return true;
        })
      );

      // Fill freed category slots
      if (remainingCatSlots !== null && cleaned.size < remainingCatSlots) {
        for (const cat of categories) {
          if (cleaned.size >= remainingCatSlots) break;
          if (!cleaned.has(cat.id)) {
            cleaned.add(cat.id);
            changed = true;
          }
        }
      }

      return changed ? cleaned : prev;
    });
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Image upload tracking ────────────────────────────────────────────────
  // Maps itemId → File object for images selected by user on item cards
  const [itemImageFiles, setItemImageFiles] = useState({});

  const handleImageUpload = (itemId, file) => {
    setItemImageFiles((prev) => ({ ...prev, [itemId]: file }));
  };

  // ── Item edit/delete handlers ────────────────────────────────────────────
  const handleDeleteItem = (itemId) => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        items: cat.items.filter((i) => i.id !== itemId),
      }))
    );
    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      for (const [catId, set] of next) {
        if (set.has(itemId)) {
          const newSet = new Set(set);
          newSet.delete(itemId);
          next.set(catId, newSet);
          break;
        }
      }
      return next;
    });
  };

  const handleCopyCategory = (e, cat) => {
    e.stopPropagation();
    const newCatId = `cat-copy-${Date.now()}`;
    const copiedItems = cat.items.map((item, idx) => ({
      ...item,
      id: `${newCatId}-item-${idx}`,
    }));
    const copiedCat = { ...cat, id: newCatId, name: `${cat.name} copy`, items: copiedItems };
    setCategories((prev) => {
      const idx = prev.findIndex((c) => c.id === cat.id);
      const next = [...prev];
      next.splice(idx + 1, 0, copiedCat);
      return next;
    });
    // Pre-check all items in the copied category
    setSelectedCategoryIds((prev) => new Set([...prev, newCatId]));
    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      next.set(newCatId, new Set(copiedItems.map((i) => i.id)));
      return next;
    });
    setSelectedCategoryId(newCatId);
  };

  const handleItemUpdate = (itemId, updatedFields) => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        items: cat.items.map((i) =>
          i.id === itemId ? { ...i, ...updatedFields } : i
        ),
      }))
    );
  };

  const buildSelectionData = () => ({
    selectedCategoryIds,
    selectedItemIds,
    menuName,
    categories,
    itemImageFiles,
  });

  return (
    <Box sx={{ position: "relative" }}>
      {/* ── Header row: title + action buttons ─────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2.5,
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.25,
            }}
          >
            {t("ocrConfirmTitle", { label })}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "#6b7280",
              textTransform: "lowercase",
            }}
          >
            {t("ocrConfirmSubtitle", { label })}
          </Typography>
        </Box>

        {/* Action buttons top-right */}
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={() => onSaveDraft?.(buildSelectionData())}
            disabled={!!isSubmitting}
            sx={{
              borderColor: "#7C3AED",
              color: "#7C3AED",
              bgcolor: "#fff",
              textTransform: "lowercase",
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.75,
              fontSize: "0.82rem",
              "&:hover": { borderColor: "#6200EE", color: "#6200EE", bgcolor: "#fff" },
            }}
          >
            {isSubmitting === "draft" ? (
              <CircularProgress size={16} sx={{ color: "#7C3AED" }} />
            ) : (
              t("ocrConfirmSaveAsDraft")
            )}
          </Button>
          <Button
            variant="contained"
            onClick={() => onConfirm?.(buildSelectionData())}
            disabled={!!isSubmitting}
            sx={{
              bgcolor: "#7C3AED",
              textTransform: "lowercase",
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.75,
              fontSize: "0.82rem",
              "&:hover": { bgcolor: "#6200EE" },
            }}
          >
            {isSubmitting === "confirm" ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              t("ocrConfirmConfirm")
            )}
          </Button>
        </Box>
      </Box>

      {/* ── Menu info: avatar + name + stats ────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          pb: 2,
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        {/* Avatar */}
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={menuData.menuImage}
            sx={{ width: 52, height: 52, bgcolor: "#F3EEFF", border: "2px solid #E9E3FF" }}
          >
            🍽️
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              bgcolor: "#7C3AED",
              color: "#fff",
              fontSize: "0.5rem",
              fontWeight: 700,
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #fff",
            }}
          >
            +3
          </Box>
        </Box>

        {/* Name + stats */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Editable menu name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
            {isEditingName ? (
              <InputBase
                value={menuName}
                onChange={(e) => setMenuName(e.target.value)}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
                autoFocus
                sx={{
                  fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  fontWeight: 700,
                  color: "#111827",
                  flex: 1,
                  textTransform: "lowercase",
                  borderBottom: "2px solid #7C3AED",
                  "& .MuiInputBase-input": { p: 0, pb: 0.25 },
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  fontWeight: 700,
                  color: "#111827",
                  textTransform: "lowercase",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {menuName}
              </Typography>
            )}
            <IconButton onClick={() => setIsEditingName(true)} size="small" sx={{ color: "#7C3AED" }}>
              <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          {/* Stats chips */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <GridViewOutlinedIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#7C3AED" }}>
                {menuData.stats?.categories ?? totalCategories}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "lowercase" }}>
                {t("ocrStatsCategoriesLabel")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <RestaurantMenuOutlinedIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#7C3AED" }}>
                {menuData.stats?.items ?? totalItems}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "lowercase" }}>
                {t("ocrStatsItemsLabel")}
              </Typography>
            </Box>
            {(menuData.stats?.pages) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ImageOutlinedIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#7C3AED" }}>
                  {menuData.stats.pages}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "lowercase" }}>
                  {t("ocrStatsPagesLabel")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── AI Summary ──────────────────────────────────────────────────────── */}
      {menuData.aiSummary && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <Typography sx={{ fontSize: "0.7rem" }}>✨</Typography>
            <Typography
              sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#111827", textTransform: "lowercase" }}
            >
              {t("ocrStatsAiSummary")}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "#6b7280",
              textTransform: "lowercase",
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {menuData.aiSummary}
          </Typography>
        </Box>
      )}

      {/* ── Duplicates banner ───────────────────────────────────────────────── */}
      {totalDuplicateCount > 0 && (
        <Box
          onClick={() => setShowDuplicateReview(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#FFF7ED",
            border: "1px solid #FED7AA",
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
            mb: 2,
            cursor: "pointer",
            "&:hover": { bgcolor: "#FFEDD5" },
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "#F97316",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: "0.6rem", color: "#fff", lineHeight: 1, fontWeight: 700 }}>
              !
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#92400E", textTransform: "lowercase" }}
            >
              {totalDuplicateCount} duplicate{totalDuplicateCount !== 1 ? "s" : ""} found
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#B45309", textTransform: "lowercase" }}>
              {duplicateCategoryGroups.length}{" "}
              {duplicateCategoryGroups.length === 1 ? "category" : "categories"} •{" "}
              {duplicateItemGroups.length} {duplicateItemGroups.length === 1 ? "item" : "items"} added
            </Typography>
          </Box>
          <ArrowForwardIosIcon sx={{ fontSize: 11, color: "#B45309", flexShrink: 0 }} />
        </Box>
      )}

      {/* ── Plan banner ─────────────────────────────────────────────────────── */}
      {isOverCatLimit ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#fff",
            border: "1px solid #C4B5FD",
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
            mb: 2,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7C3AED", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#111827", textTransform: "lowercase" }}
            >
              {t("ocrConfirmUnlockTitle", { label })}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#6b7280", textTransform: "lowercase" }}>
              {t("ocrConfirmOverLimitStats", {
                totalCats: existingCategoryCount + selectedCatCount,
                maxCats,
                totalItems: existingItemCount + selectedItemCount,
                maxItems: planLimits?.maxItems ?? totalItems,
              })}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            onClick={onUpgrade}
            sx={{
              bgcolor: "#7C3AED",
              color: "#fff",
              textTransform: "lowercase",
              fontWeight: 600,
              borderRadius: 1.5,
              px: 1,
              py: 0.25,
              fontSize: "0.68rem",
              flexShrink: 0,
              minWidth: "auto",
              "&:hover": { bgcolor: "#6200EE" },
            }}
          >
            upgrade now
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "#F59E0B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: "0.55rem", color: "#fff", lineHeight: 1 }}>✓</Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#92400E", textTransform: "lowercase" }}
            >
              {t("ocrConfirmReadyTitle", { label })}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#B45309", textTransform: "lowercase" }}>
              {t("ocrConfirmBelowLimitStats", {
                cats: selectedCatCount,
                maxCats: remainingCatSlots ?? "",
                items: selectedItemCount,
                maxItems: remainingItemSlots ?? "",
              })}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Category tabs (horizontal scroll) ───────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          overflowX: "auto",
          pb: 1,
          mb: 2.5,
          "&::-webkit-scrollbar": { height: "4px" },
          "&::-webkit-scrollbar-track": { bgcolor: "#f1f1f1", borderRadius: "2px" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#c1c1c1", borderRadius: "2px" },
          scrollbarWidth: "thin",
          scrollbarColor: "#c1c1c1 #f1f1f1",
        }}
      >
        {categories.map((cat) => {
          const isActive = selectedCategoryId === cat.id;
          const isChecked = selectedCategoryIds.has(cat.id);
          return (
            <Box
              key={cat.id}
              onClick={() => setSelectedCategoryId(cat.id)}
              sx={{
                flexShrink: 0,
                minWidth: 110,
                bgcolor: isActive ? "#7C3AED" : "#f9fafb",
                border: isActive ? "1.5px solid #7C3AED" : "1.5px solid #e5e7eb",
                borderRadius: 2.5,
                px: 1.5,
                py: 1.25,
                cursor: "pointer",
                position: "relative",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: "#7C3AED",
                  bgcolor: isActive ? "#7C3AED" : "#F3EEFF",
                },
              }}
            >
              {/* Checkbox for include/exclude */}
              <Checkbox
                checked={isChecked}
                onChange={(e) => handleToggleCategory(e, cat.id)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  p: 0.25,
                  color: isActive ? "rgba(255,255,255,0.7)" : "#d1d5db",
                  "&.Mui-checked": { color: isActive ? "#fff" : "#7C3AED" },
                  "& .MuiSvgIcon-root": { fontSize: 14 },
                }}
              />
              <RestaurantMenuOutlinedIcon
                sx={{ fontSize: 18, color: isActive ? "rgba(255,255,255,0.85)" : "#9ca3af", mb: 0.5 }}
              />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: isActive ? "#fff" : "#111827",
                  textTransform: "lowercase",
                  lineHeight: 1.3,
                  pr: 2,
                }}
              >
                {cat.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: isActive ? "rgba(255,255,255,0.75)" : "#6b7280",
                  textTransform: "lowercase",
                }}
              >
                {cat.items.length} {t("ocrStatsItemsLabel")}
              </Typography>
              {/* Copy category button */}
              <IconButton
                onClick={(e) => handleCopyCategory(e, cat)}
                size="small"
                title="copy category"
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  p: 0.25,
                  color: isActive ? "rgba(255,255,255,0.6)" : "#d1d5db",
                  "&:hover": {
                    color: isActive ? "#fff" : "#7C3AED",
                    bgcolor: "transparent",
                  },
                }}
              >
                <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
              </IconButton>

              {/* Duplicate badge */}
              {catHasDuplicate(cat) && (
                <Chip
                  label="duplicate"
                  size="small"
                  sx={{
                    height: 14,
                    mt: 0.5,
                    fontSize: "0.55rem",
                    bgcolor: isActive ? "rgba(249,115,22,0.25)" : "#FEF3C7",
                    color: isActive ? "#fff" : "#92400E",
                    fontWeight: 700,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Items grid (3 columns) ───────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 2,
          maxHeight: { xs: "auto", md: 480 },
          overflowY: { md: "auto" },
          pr: { md: 0.5 },
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { bgcolor: "#f1f1f1", borderRadius: "2px" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#c1c1c1", borderRadius: "2px" },
          scrollbarWidth: "thin",
          scrollbarColor: "#c1c1c1 #f1f1f1",
        }}
      >
        {items.map((item) => {
          const isItemChecked = selectedItemIds.get(selectedCategoryId)?.has(item.id) ?? false;
          return (
            <Box key={item.id} sx={{ position: "relative" }}>
              {/* Checkbox overlay on card top-left */}
              <Checkbox
                checked={isItemChecked}
                onChange={() => handleToggleItem(item.id)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  zIndex: 2,
                  bgcolor: "rgba(255,255,255,0.85)",
                  borderRadius: 1,
                  p: 0.25,
                  color: "#7C3AED",
                  "&.Mui-checked": { color: "#7C3AED" },
                }}
              />
              <MenuItemCard
                item={item}
                currencySymbol={currencySymbol}
                onDelete={handleDeleteItem}
                onItemUpdate={handleItemUpdate}
                onImageUpload={handleImageUpload}
              />
            </Box>
          );
        })}
      </Box>

      {/* ── Duplicate review overlay ─────────────────────────────────────────── */}
      {showDuplicateReview && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: "100%",
            bgcolor: "#fff",
            zIndex: 10,
            p: 0.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DuplicateReviewModal
            itemGroups={duplicateItemGroups}
            categoryGroups={duplicateCategoryGroups}
            currencySymbol={currencySymbol}
            onMergeItem={handleMergeItem}
            onKeepBothItems={handleKeepBothItems}
            onMergeCategory={handleMergeCategory}
            onKeepBothCategories={handleKeepBothCategories}
            onUpdateOcrItem={handleUpdateOcrItem}
            onClose={() => setShowDuplicateReview(false)}
          />
        </Box>
      )}
    </Box>
  );
};

export default ConfirmMenu;
